import {
  analysisAppliedControlParams,
  analysisControlParamLabels,
  analysisControlParamNames,
  analysisPublicationSupport,
  compileAnalysisGraph,
  compileAnalysisSheet,
  type AnalysisPublicationSupport,
  type AnalysisPublicationTargets,
  type CompileAnalysisRecipeOptions,
} from '@/helpers/analysisRecipeCompiler.helpers';
import {
  analysisColumnLabels,
  buildAnalysisColumnDisplay,
} from '@/helpers/analysisColumnDisplay.helpers';
import type { AnalysisRecipeState, AnalysisSheetRecipe } from '@/types/analysisRecipe.types';
import { analysisSourceRecipe } from '@/helpers/analysisSheetShell.helpers';
import { ANALYSIS_RECIPE_SCHEMA_VERSION } from '@/types/analysisRecipe.types';
import type { AnalysisCardConfig, ColumnFormatConfig } from '@/types/dashboard';
import type {
  Pipeline,
  PipelineParamValue,
} from '@/interfaces/pipelines.interface';

/** First page a published Analysis table card requests. */
export const ANALYSIS_CARD_PAGE_SIZE = 50;

/**
 * Everything a publication surface needs from one Analysis sheet: the pipelines
 * each output would store, the control values to seed a card with, and which
 * outputs are actually available. Compiled from the sheet rather than from the
 * grid's current page, so a card starts at the first row in a total order and
 * can page without repeating or skipping rows.
 */
export interface AnalysisPublicationSurface {
  support: AnalysisPublicationSupport;
  /** Deterministically ordered table pipeline a card stores and pages. */
  tablePipeline: Pipeline;
  /** Induced-graph pipeline, or null when the recipe keeps no source nodes. */
  graphPipeline: Pipeline | null;
  /** Why a graph is impossible for this recipe, in the user's terms. */
  graphBlockers: string[];
  outputColumns: string[];
  /** Display label per output column, as the sheet shows it. */
  columnLabels: Record<string, string>;
  /**
   * A reader label for every column the pipelines generate, internal ones
   * included. A card sends it as its run's `column_labels`, never as a heading.
   */
  pipelineColumnLabels: Record<string, string>;
  /** Dashboard display formats keyed by the published display labels. */
  columnFormats?: ColumnFormatConfig[];
  /** `null` for a total grain — one row, so no row identity to publish. */
  rowKey: string | null;
  /** Applied control values, including ones inherited from a parent sheet. */
  paramValues: Record<string, PipelineParamValue>;
  /** Every control param the lineage declares, applied or not. */
  paramNames: string[];
  /** Reader-facing label per control param; the name itself is a wire id. */
  paramLabels: Record<string, string>;
  isBreakdown: boolean;
  sheetName: string;
  sourceLabel?: string;
  /** The sheet with its family, for a card to reopen it in reMap. */
  sourceRecipe?: AnalysisRecipeState;
}

/**
 * What to tell the author when a graph card could not hold the whole sheet.
 *
 * `null` when nothing was cut. A graph card materialises its nodes under a cap,
 * so a sheet larger than the cap publishes a card showing part of itself — and
 * a part presented as a whole is a wrong answer with a correct implementation.
 * The backend reports the clipping; this turns it into something the author
 * reads before the card exists.
 */
export function analysisGraphTruncationWarning(input: {
  truncated: boolean;
  nodeCount: number;
}): string | null {
  if (!input.truncated) return null;
  return `This sheet has more records than a graph card can draw, so the card shows ${input.nodeCount.toLocaleString()} of them. Narrow the sheet if the whole set matters.`;
}

export function buildAnalysisPublicationSurface(input: {
  sheet: AnalysisSheetRecipe;
  sheets?: Record<string, AnalysisSheetRecipe>;
  options: CompileAnalysisRecipeOptions;
  verified?: AnalysisPublicationTargets;
}): AnalysisPublicationSurface {
  const { sheet, sheets, options } = input;
  const publicationOptions: CompileAnalysisRecipeOptions = {
    ...options,
    target: 'publication',
    offset: 0,
    limit: options.limit ?? ANALYSIS_CARD_PAGE_SIZE,
  };
  const table = compileAnalysisSheet(sheet, sheets, publicationOptions);
  const graph = compileAnalysisGraph(sheet, sheets, options);
  const displays = buildAnalysisColumnDisplay(
    sheet,
    sheet.breakdown ? (sheets?.[sheet.breakdown.parentSheetId] ?? null) : null,
  );
  return {
    support: analysisPublicationSupport({
      compiled: table,
      graph,
      verified: input.verified,
    }),
    tablePipeline: table.pipeline,
    graphPipeline: graph.pipeline,
    graphBlockers: graph.blockers,
    outputColumns: table.outputColumns,
    columnLabels: analysisColumnLabels(displays),
    // The graph pipeline is a prefix of the table one, so these cover both.
    pipelineColumnLabels: table.columnLabels,
    // The card's own formatter, handed the sheet's numbers. A figure that reads
    // `0.02%` on the sheet and `0.0%` on the card published from it is the
    // failure this mirrors away: `display.decimals` is the author's choice, and
    // the literal is only the default they did not override.
    //
    // Keyed by label, which is what `projectAnalysisRowsForDashboard` renames
    // the runtime columns to. It cannot always use the plain label: when two
    // columns collide on one and both hold real, differing values, the computed
    // one is disambiguated to `<label> (computed)` rather than destroying a
    // value. Whether a collision is lossy depends on the rows, so it is
    // unknowable here — the projection reports what it renamed, and the publish
    // path re-keys these entries onto the headings it produced.
    columnFormats: Object.values(displays).flatMap((display) =>
      display.format === 'percent'
        ? [
            {
              column: display.label,
              type: 'number' as const,
              decimals: display.decimals ?? 1,
              // `{VALUE}%` keeps the card reading exactly as the sheet does
              // (`83.3%`); a bare `%` marker is appended after a space.
              unitMarker: '{VALUE}%',
            },
          ]
        : display.format === 'rank'
          ? [{ column: display.label, type: 'number' as const, decimals: 0 }]
          : display.format === 'number'
            ? // Emitted even with no chosen precision. With no config at all the
              // card falls to `defaultBaseString` → `String(value)` and prints
              // the raw float — `483261.16999999987` where the sheet reads
              // `483,261.17`, the very figure this formatting exists to tame. A
              // `type: 'number'` config with `decimals` omitted resolves to
              // `Intl.NumberFormat` defaults (0–3 fraction digits), which is
              // exactly what `toLocaleString()` gives the sheet, so the two
              // agree by construction rather than by luck.
              [
                {
                  column: display.label,
                  type: 'number' as const,
                  ...(display.decimals !== undefined
                    ? { decimals: display.decimals }
                    : {}),
                },
              ]
            : [],
    ),
    rowKey: table.rowKey,
    paramValues: analysisAppliedControlParams(sheet, sheets),
    paramNames: analysisControlParamNames(sheet, sheets),
    paramLabels: analysisControlParamLabels(
      sheet,
      sheets,
      (field) => displays[field]?.label ?? field,
    ),
    isBreakdown: Boolean(sheet.breakdown),
    sheetName: sheet.name,
    sourceLabel: sheet.source.label,
    sourceRecipe: analysisSourceRecipe(sheet, sheets),
  };
}

/**
 * Analysis metadata persisted on a card. `rowsPolicy` follows the output: a
 * table card pages, while a chart or KPI has to see every row before it can
 * state a total or draw a distribution.
 *
 * `pinnedParams` names the params the card owns outright. They come from a
 * pinned Analysis chart, which was pinned precisely so its value would stop
 * moving — a dashboard global must not take it back over.
 */
export function buildAnalysisCardMetadata(
  surface: AnalysisPublicationSurface,
  rowsPolicy: AnalysisCardConfig['rowsPolicy'],
  pinnedParams?: Record<string, PipelineParamValue>,
): AnalysisCardConfig {
  const pinnedNames = Object.keys(pinnedParams ?? {});
  return {
    sheetName: surface.sheetName,
    ...(surface.sourceLabel ? { sourceLabel: surface.sourceLabel } : {}),
    outputColumns: surface.outputColumns,
    ...(Object.keys(surface.columnLabels).length > 0
      ? { columnLabels: surface.columnLabels }
      : {}),
    ...(surface.rowKey !== null ? { rowKey: surface.rowKey } : {}),
    isBreakdown: surface.isBreakdown,
    rowsPolicy,
    ...(surface.paramNames.length > 0
      ? { paramNames: surface.paramNames }
      : {}),
    ...(Object.keys(surface.paramLabels).length > 0
      ? { paramLabels: surface.paramLabels }
      : {}),
    ...(pinnedNames.length > 0 ? { pinnedParams: pinnedNames } : {}),
    recipeSchemaVersion: ANALYSIS_RECIPE_SCHEMA_VERSION,
    ...(surface.sourceRecipe ? { sourceRecipe: surface.sourceRecipe } : {}),
  };
}

/**
 * How a card of this visualization must fetch its rows.
 *
 * A table pages, because the user walks through it. Everything else — a chart, a
 * single-value card — has to see the whole result before it can be drawn, or it
 * plots a 50-row window as if it were the answer.
 */
export function rowsPolicyForVisualization(
  visualization: string,
): AnalysisCardConfig['rowsPolicy'] {
  return visualization === 'table' ? 'page' : 'materialize';
}

/**
 * A published card's pipeline config, corrected for the visualization it is
 * being saved with.
 *
 * Editing a card re-attached its stored config verbatim, so switching an
 * Analysis table to a chart kept `rowsPolicy: 'page'` and the chart was drawn
 * from one page with no paginator and no warning. Only the row policy is
 * touched, and only for Analysis cards: every other source, and any config that
 * already agrees, is returned unchanged so the edit stays a no-op.
 */
export function reconcilePipelineConfigForVisualization<
  T extends {
    source?: string;
    analysis?: AnalysisCardConfig;
  },
>(config: T, visualization: string): T {
  if (config.source !== 'analysis' || !config.analysis) return config;
  const rowsPolicy = rowsPolicyForVisualization(visualization);
  if (config.analysis.rowsPolicy === rowsPolicy) return config;
  return { ...config, analysis: { ...config.analysis, rowsPolicy } };
}
